/**
 * The error every failed TMR API call throws.
 *
 * The backend answers failures with `ProblemDetails` carrying a machine-readable `code` extension
 * (`Shared.Api/ApiProblem`): `code` is a stable English identifier, while `title`/`detail` are the
 * PT-BR text an operator reads. Screens branch on `code` — never on the message — so that two
 * different 409s (duplicate CNPJ vs. duplicate subdomain) stay distinguishable.
 */
export class ApiError extends Error {
  constructor(
    /** HTTP status of the response. */
    readonly status: number,
    /** Machine-readable code from the problem body; null when the response carried none. */
    readonly code: string | null,
    /** Operator-facing detail (PT-BR), already suitable for display. */
    readonly detail: string,
    /** Short operator-facing title (PT-BR). */
    readonly title: string | null = null,
    /**
     * Per-field messages when the endpoint reports them (`errors` of a validation problem), keyed by the
     * request's JSON path — e.g. `clinic.cnpj`. Empty for every other failure.
     */
    readonly fieldErrors: Readonly<Record<string, string[]>> = {},
    /**
     * The problem body's own members beyond the standard ones (`title`, `detail`, `status`, `type`,
     * `instance`, `code`, `errors`, `traceId`) — data an endpoint attaches to one specific failure, such as
     * the `legacyUserId` of a 409 that lets the screen offer the next step. Empty when there are none.
     */
    readonly extensions: Readonly<Record<string, unknown>> = {},
  ) {
    super(detail);
    this.name = 'ApiError';
  }

  /** True when the session is gone — expired, revoked, or the user was disabled (05 §5). */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** True when the operator is authenticated but lacks the permission the endpoint requires. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** True when the request lost a race or violated a uniqueness rule; branch on {@link code} for which. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  /** True when the addressed resource does not exist. */
  get isNotFound(): boolean {
    return this.status === 404;
  }
}

/** The problem body shape the API returns (RFC 7807 plus the `code` extension). */
export interface ProblemBody {
  title?: string;
  detail?: string;
  status?: number;
  code?: string;
  /** Validation problems only: messages per field, keyed by the request's JSON path. */
  errors?: Record<string, string[]>;
}

/** Fallback text when a failure carries no readable body (network error, proxy error page, 502…). */
export const GENERIC_ERROR_DETAIL = 'Não foi possível concluir a operação. Tente novamente.';
